'use client';

import type { ComponentProps } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@ifixit/ui/lib/utils';
import { FaIcon } from './icon';

export function Dialog({ ...props }: ComponentProps<typeof DialogPrimitive.Root>) {
   return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

export function DialogTrigger({ ...props }: ComponentProps<typeof DialogPrimitive.Trigger>) {
   return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

export function DialogPortal({ ...props }: ComponentProps<typeof DialogPrimitive.Portal>) {
   return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

export function DialogClose({ ...props }: ComponentProps<typeof DialogPrimitive.Close>) {
   return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

export function DialogContentPrimitive({
   ...props
}: ComponentProps<typeof DialogPrimitive.Content>) {
   return <DialogPrimitive.Content data-slot="dialog-content" {...props} />;
}

export function DialogOverlay({
   className,
   ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
   return (
      <DialogPrimitive.Overlay
         data-slot="dialog-overlay"
         className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
            className
         )}
         {...props}
      />
   );
}

export function DialogContent({
   className,
   children,
   showCloseButton = true,
   overlayClassName,
   ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
   showCloseButton?: boolean;
   /** Extra classes for the backdrop, e.g. a z-index override when the dialog
    * must stack above fixed page chrome that sits over the default z-50. */
   overlayClassName?: string;
}) {
   return (
      <DialogPortal data-slot="dialog-portal">
         <DialogOverlay className={overlayClassName} />
         <DialogPrimitive.Content
            data-slot="dialog-content"
            className={cn(
               'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-2xl border p-6 shadow-lg duration-200 sm:max-w-lg',
               className
            )}
            {...props}
         >
            {children}
            {showCloseButton && (
               <DialogPrimitive.Close
                  data-slot="dialog-close"
                  className="button-ghost button-icon absolute top-1.5 right-1.5 text-gray-500 after:absolute after:-inset-1 hover:text-accent-foreground"
               >
                  <FaIcon name="xmark" />
                  <span className="sr-only">Close</span>
               </DialogPrimitive.Close>
            )}
         </DialogPrimitive.Content>
      </DialogPortal>
   );
}

export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="dialog-header"
         className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
         {...props}
      />
   );
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="dialog-footer"
         className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
         {...props}
      />
   );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
   return (
      <DialogPrimitive.Title
         data-slot="dialog-title"
         className={cn('text-lg leading-none font-semibold', className)}
         {...props}
      />
   );
}

export function DialogDescription({
   className,
   ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
   return (
      <DialogPrimitive.Description
         data-slot="dialog-description"
         className={cn('text-muted-foreground text-sm', className)}
         {...props}
      />
   );
}
